import { useState } from 'react'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { mdiPlus } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import {
    KNOWLEDGE_KINDS,
    useCreateOrganizationNote,
    useDeleteOrganizationNote,
    useKnowledgeProposalCount,
    useOrganizationKnowledge,
    useUpdateOrganizationNote,
} from '../../hooks/useKnowledge'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { EmptyState } from '../ui/empty-state'
import { Input } from '../ui/input'
import { NoteCard } from './NoteCard'
import { NoteForm } from './NoteForm'
import { KnowledgeProposalsTab } from './KnowledgeProposalsTab'

/**
 * The organization's knowledge (spec 2026-10-10-06): rules that apply
 * everywhere ("every intervention over 2 000 EUR needs two quotes"). Searching
 * widens the list to the asset notes the caller can read, each linking to the
 * asset it lives on. The search and the kind filter live in the URL.
 */
export function KnowledgePage() {
    const { org } = useParams({ from: '/$org/knowledge' })
    const search = useSearch({ from: '/$org/knowledge' })
    const navigate = useNavigate()
    const { t } = useTranslation('knowledge')
    useBreadcrumbs([{ title: t('common:nav.home'), path: `/${org}` }, { title: t('title') }])
    const canWrite = useCan('knowledge.write')
    const q = search.q ?? ''
    const kind = search.kind ?? ''
    const { data: notes, isLoading, error } = useOrganizationKnowledge(org, q, kind)
    const [editing, setEditing] = useState<string | 'new' | null>(null)
    const create = useCreateOrganizationNote(org)
    const update = useUpdateOrganizationNote(org)
    const remove = useDeleteOrganizationNote(org)
    const [draft, setDraft] = useState(q)
    const onProposals = search.tab === 'proposals'
    const { data: pendingCount } = useKnowledgeProposalCount(org, true)

    const go = (next: { q?: string; kind?: string }) =>
        navigate({
            to: '/$org/knowledge',
            params: { org },
            search: {
                q: next.q !== undefined ? next.q || undefined : search.q,
                kind: next.kind !== undefined ? next.kind || undefined : search.kind,
                tab: undefined,
                proposal: undefined,
                note: search.note,
            },
        })
    const showTab = (tab: 'notes' | 'proposals') =>
        navigate({
            to: '/$org/knowledge',
            params: { org },
            search: tab === 'proposals' ? { tab: 'proposals' } : { q: search.q, kind: search.kind },
        })

    return (
        <div className="space-y-6" data-testid="knowledge-page">
            <PageHeader
                icon={SECTION_ICONS.knowledge}
                title={t('title')}
                description={t('description')}
                actions={
                    canWrite && editing === null && !onProposals ? (
                        <Button
                            onClick={() => setEditing('new')}
                            aria-label={t('add_note')}
                            title={t('add_note')}
                            data-testid="knowledge-add-btn"
                        >
                            <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('add_note')}</span>
                        </Button>
                    ) : undefined
                }
            />
            <div className="flex gap-1 border-b border-border" role="tablist" data-testid="knowledge-tabs">
                <Button
                    role="tab"
                    variant="ghost"
                    aria-selected={!onProposals}
                    className={`rounded-none border-b-2 ${!onProposals ? 'border-primary' : 'border-transparent'}`}
                    onClick={() => void showTab('notes')}
                    data-testid="knowledge-tab-notes"
                >
                    {t('tab_notes')}
                </Button>
                <Button
                    role="tab"
                    variant="ghost"
                    aria-selected={onProposals}
                    className={`rounded-none border-b-2 ${onProposals ? 'border-primary' : 'border-transparent'}`}
                    onClick={() => void showTab('proposals')}
                    data-testid="knowledge-tab-proposals"
                >
                    {t('tab_proposals')}
                    {!!pendingCount && (
                        <span
                            className="ml-1.5 rounded-full bg-primary px-1.5 text-xs text-primary-foreground"
                            data-testid="knowledge-proposals-count"
                        >
                            {pendingCount}
                        </span>
                    )}
                </Button>
            </div>
            {onProposals ? (
                <KnowledgeProposalsTab org={org} proposalUid={search.proposal} />
            ) : (
            <>
            <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                    e.preventDefault()
                    void go({ q: draft.trim() })
                }}
            >
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t('search_placeholder')}
                    className="max-w-sm"
                    aria-label={t('search_placeholder')}
                    data-testid="knowledge-search-input"
                />
                <Button type="submit" variant="outline" size="sm" data-testid="knowledge-search-btn">
                    {t('search')}
                </Button>
                <div className="flex flex-wrap gap-1" role="tablist">
                    {(['', ...KNOWLEDGE_KINDS] as const).map((k) => (
                        <Button
                            key={k || 'all'}
                            role="tab"
                            aria-selected={kind === k}
                            variant={kind === k ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => void go({ kind: k })}
                            data-testid={`knowledge-filter-${k || 'all'}`}
                        >
                            {k ? t(`kind.${k}`) : t('filter_all')}
                        </Button>
                    ))}
                </div>
            </form>

            {editing === 'new' && (
                <NoteForm
                    withSubtreeOption={false}
                    testIdPrefix="new-note"
                    onCancel={() => setEditing(null)}
                    onSubmit={async (input) => {
                        await create.mutateAsync(input)
                        setEditing(null)
                    }}
                />
            )}

            {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : error ? (
                <p className="text-destructive" role="alert">{t('load_failed')}</p>
            ) : (notes ?? []).length === 0 ? (
                <EmptyState
                    icon={SECTION_ICONS.knowledge}
                    title={q ? t('empty_search') : t('empty_all')}
                    description={q ? undefined : t('empty_org_description')}
                    data-testid="knowledge-empty"
                />
            ) : (
                <ul className="space-y-2" data-testid="knowledge-list">
                    {(notes ?? []).map((note) =>
                        editing === note.uid ? (
                            <li key={note.uid}>
                                <NoteForm
                                    note={note}
                                    withSubtreeOption={false}
                                    testIdPrefix="edit-note"
                                    onCancel={() => setEditing(null)}
                                    onSubmit={async (input) => {
                                        await update.mutateAsync({ noteUid: note.uid, ...input })
                                        setEditing(null)
                                    }}
                                />
                            </li>
                        ) : (
                            <NoteCard
                                key={note.uid}
                                org={org}
                                note={note}
                                // An asset note is edited on its asset.
                                canWrite={canWrite && note.source.type === 'organization'}
                                showSource
                                onEdit={() => setEditing(note.uid)}
                                onDelete={() => remove.mutate(note.uid)}
                                deleting={remove.isPending}
                                highlighted={note.uid === search.note}
                            />
                        ),
                    )}
                </ul>
            )}
            </>
            )}
        </div>
    )
}
